/**
 * Shared display helpers for user announcements — type/status badge colors and
 * translation resolution. Used by the authoring UI and the delivery modal/bell.
 */
import type {
  SfAnnouncementStatus,
  SfAnnouncementType,
  SfUserAnnouncementTranslation,
} from '../types';

export const DEFAULT_ANNOUNCEMENT_LANGUAGE = 'en';

/** Badge background/text classes per announcement type (mirrors changelog category styling). */
export const announcementTypeColors: Record<SfAnnouncementType, string> = {
  feature: 'bg-info/10 text-info',
  improvement: 'bg-success/10 text-success',
  change: 'bg-warning/10 text-warning',
  outage: 'bg-destructive/10 text-destructive',
  price_change: 'bg-warning/10 text-warning',
  general: 'bg-muted text-muted-foreground',
};

/** Outline-badge text/border classes per status. */
export const announcementStatusColors: Record<SfAnnouncementStatus, string> = {
  draft: 'text-muted-foreground',
  published: 'border-success text-success',
  archived: 'text-muted-foreground line-through',
};

/**
 * Pick the subject for a language, falling back to English. Returns null when
 * neither the requested language nor English has a translation.
 */
export function resolveAnnouncementSubject(
  translations: SfUserAnnouncementTranslation[],
  language: string = DEFAULT_ANNOUNCEMENT_LANGUAGE,
): string | null {
  const byLanguage = new Map(translations.map((tr) => [tr.language, tr]));
  const resolved = byLanguage.get(language) ?? byLanguage.get(DEFAULT_ANNOUNCEMENT_LANGUAGE);
  return resolved?.subject ?? null;
}
